import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  PermissionCode,
  SystemAccessProfileCode,
  SYSTEM_ACCESS_PROFILE_MAP,
  getSystemProfilePermissions,
} from '../constants/permissions';

const STORAGE_KEY = 'check.rbac.profilePreview';

type ProfilePreviewContextValue = {
  previewProfileCode: SystemAccessProfileCode | null;
  previewProfileName: string | null;
  isPreviewActive: boolean;
  setPreviewProfile: (code: SystemAccessProfileCode | null) => void;
  clearPreview: () => void;
  effectivePermissions: PermissionCode[];
};

const ProfilePreviewContext = createContext<ProfilePreviewContextValue | null>(null);

function readStoredPreview(): SystemAccessProfileCode | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw || raw === 'admin') return null;
    return raw as SystemAccessProfileCode;
  } catch {
    return null;
  }
}

type ProfilePreviewProviderProps = {
  children: ReactNode;
  loginPermissions?: string[];
};

export function ProfilePreviewProvider({
  children,
  loginPermissions = [],
}: ProfilePreviewProviderProps) {
  const [previewProfileCode, setPreviewProfileCode] = useState<SystemAccessProfileCode | null>(
    () => readStoredPreview()
  );

  useEffect(() => {
    try {
      if (previewProfileCode) {
        sessionStorage.setItem(STORAGE_KEY, previewProfileCode);
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // ignore storage errors
    }
  }, [previewProfileCode]);

  const setPreviewProfile = useCallback((code: SystemAccessProfileCode | null) => {
    setPreviewProfileCode(code && code !== 'admin' ? code : null);
  }, []);

  const clearPreview = useCallback(() => {
    setPreviewProfileCode(null);
  }, []);

  const effectivePermissions = useMemo(() => {
    if (previewProfileCode) {
      return getSystemProfilePermissions(previewProfileCode);
    }
    return (loginPermissions ?? []) as PermissionCode[];
  }, [loginPermissions, previewProfileCode]);

  const previewProfileName = previewProfileCode
    ? SYSTEM_ACCESS_PROFILE_MAP[previewProfileCode]?.name ?? null
    : null;

  const value = useMemo(
    () => ({
      previewProfileCode,
      previewProfileName,
      isPreviewActive: !!previewProfileCode,
      setPreviewProfile,
      clearPreview,
      effectivePermissions,
    }),
    [
      clearPreview,
      effectivePermissions,
      previewProfileCode,
      previewProfileName,
      setPreviewProfile,
    ]
  );

  return (
    <ProfilePreviewContext.Provider value={value}>{children}</ProfilePreviewContext.Provider>
  );
}

export function useProfilePreview() {
  const context = useContext(ProfilePreviewContext);
  if (!context) {
    throw new Error('useProfilePreview must be used within ProfilePreviewProvider');
  }
  return context;
}

export function useProfilePreviewOptional() {
  return useContext(ProfilePreviewContext);
}
